import type { AssistantChatRequest, AssistantChatResponse } from '../ai-assistant.service';

const DEMO_RESPONSES: Record<string, AssistantChatResponse> = {
  'list assets': {
    id: 'demo-1',
    content: 'I found 183 assets in your inventory. Here\'s a summary:\n\n- **Computer Equipment**: 75 assets (£125,000 total value)\n- **Furniture & Fixtures**: 58 assets (£45,000 total value)\n- **Vehicles**: 50 assets (£280,000 total value)\n\nWould you like me to show details for a specific category or filter by location?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-1',
      name: 'List Assets',
      status: 'success',
      result: { totalAssets: 183, categories: 3 },
    },
  },
  'depreciation': {
    id: 'demo-2',
    content: 'Here\'s your depreciation summary for the current period:\n\n- **Pending batches**: 2 batches awaiting approval\n- **Total depreciation this period**: £12,450\n- **Assets eligible**: 145 assets\n\nWould you like me to show the pending batches or process depreciation for specific assets?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-2',
      name: 'Get Depreciation Summary',
      status: 'success',
      result: { pendingBatches: 2, totalAmount: 12450 },
    },
  },
  'audit': {
    id: 'demo-3',
    content: 'You have 3 active audits:\n\n1. **Q1 2026 Physical Audit** - 65% complete (London HQ)\n2. **IT Equipment Verification** - 30% complete (All locations)\n3. **Vehicle Fleet Check** - Not started (Fleet depot)\n\nWould you like me to show details for any of these audits or create a new one?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-3',
      name: 'List Audits',
      status: 'success',
      result: { activeAudits: 3 },
    },
  },
  'compliance': {
    id: 'demo-4',
    content: 'Compliance overview:\n\n- **Overdue inspections**: 5 assets\n- **Due this week**: 12 assets\n- **Compliant**: 166 assets (90.7%)\n\nThe overdue items include 3 PAT tests and 2 LOLER inspections. Would you like me to show the details or schedule inspections?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-4',
      name: 'Get Compliance Dashboard',
      status: 'success',
      result: { overdue: 5, dueThisWeek: 12, compliant: 166 },
    },
  },
  'alerts': {
    id: 'demo-5',
    content: 'You have 8 active alerts:\n\n- **High priority**: 2 (warranty expiring, insurance renewal)\n- **Medium priority**: 4 (maintenance due, depreciation review)\n- **Low priority**: 2 (asset review reminders)\n\nWould you like me to show details or acknowledge any alerts?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-5',
      name: 'List Alerts',
      status: 'success',
      result: { totalAlerts: 8, highPriority: 2 },
    },
  },
  'create asset': {
    id: 'demo-6',
    content: 'I can help you create a new asset. I\'ll need the following information:\n\n1. **Asset Code** (or I can generate one)\n2. **Asset Name**\n3. **Category**\n4. **Location**\n5. **Acquisition Cost**\n6. **Acquisition Date**\n\nWould you like me to generate an asset code, or do you have one ready?',
    timestamp: new Date().toISOString(),
  },
  'default': {
    id: 'demo-default',
    content: 'I\'m the Asset Manager Assistant. I can help you with:\n\n- **Asset Management**: Search, create, update, and manage assets\n- **Depreciation**: View batches, process depreciation, post to financials\n- **Audits**: Create and manage physical asset audits\n- **Compliance**: Track inspections and regulatory requirements\n- **Alerts**: View and manage system alerts\n- **Reports**: Generate summaries and insights\n\nWhat would you like to do?',
    timestamp: new Date().toISOString(),
  },
};

function findBestMatch(message: string): AssistantChatResponse {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('asset') && (lowerMessage.includes('list') || lowerMessage.includes('show') || lowerMessage.includes('all'))) {
    return { ...DEMO_RESPONSES['list assets'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('depreciation') || lowerMessage.includes('depreciate')) {
    return { ...DEMO_RESPONSES['depreciation'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('audit')) {
    return { ...DEMO_RESPONSES['audit'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('compliance') || lowerMessage.includes('inspection')) {
    return { ...DEMO_RESPONSES['compliance'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('alert')) {
    return { ...DEMO_RESPONSES['alerts'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('create') && lowerMessage.includes('asset')) {
    return { ...DEMO_RESPONSES['create asset'], id: generateId(), timestamp: new Date().toISOString() };
  }
  
  return { ...DEMO_RESPONSES['default'], id: generateId(), timestamp: new Date().toISOString() };
}

function generateId(): string {
  return `demo-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

class AIAssistantDemoService {
  async sendMessage(request: AssistantChatRequest, signal?: AbortSignal): Promise<AssistantChatResponse> {
    await this.simulateDelay(signal);
    return findBestMatch(request.message);
  }

  async checkHealth(): Promise<boolean> {
    return true;
  }

  private simulateDelay(signal?: AbortSignal): Promise<void> {
    const delay = 500 + Math.random() * 1000;
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(resolve, delay);
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }
    });
  }
}

export const aiAssistantDemoService = new AIAssistantDemoService();
